import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@shopify/polaris';
import {
  SearchIcon,
  XSmallIcon,
  ClockIcon,
  SearchRecentIcon,
  ExternalIcon,
  ClipboardIcon,
  LinkIcon,
  ArrowRightIcon,
  PlusCircleIcon,
  OrganizationIcon,
  EmailIcon,
  PhoneIcon,
  GlobeIcon,
  FileIcon,
  LightbulbIcon,
  AlertTriangleIcon,
  ReturnIcon,
  ReceiptEuroIcon,
  OrderIcon
} from '@shopify/polaris-icons';
import { useAuthStore, useLookupStore } from '../../context/useStore';
import userRoles from '../../utils/userRoles';
import { useCommandPaletteStore, takeFocusBeforeOpen, MOD_KEY, SHORTCUT_LABEL } from './store';
import { SCOPES, scopeByKey, typeInfo, DEEP_GROUPS, groupLabel } from './searchTypes';
import { buildCommands, QUICK_CREATE_URLS, QUICK_PAGE_URLS } from './commands';
import { highlight, queryWords, rankCommands } from './fuzzy';
import { loadRecentRecords, rememberRecord, forgetRecord, loadRecentQueries, rememberQuery } from './recents';
import { useSearchStream } from './useSearchStream';
import { useDayStats } from './useDayStats';
import './CommandPalette.css';

/*
  The Cmd+K palette: one search over every record type (server/services/search), plus the app's pages and actions.
  Opens with Cmd/Ctrl+K, "/" outside a text field, or the top bar search field (PageWrapper.jsx).

  Typing "> " limits it to pages & actions, "@ " to companies & contacts; Tab cycles the filters. Exact identifiers
  (document numbers, OGM, VAT, IBAN, phone, e-mail) come back as top hits; lines, notes and file names stream in last.
*/

const PREFIX_SCOPES = { '>': 'commands', '@': 'crm' };
const ALL_LIMIT = 5;
const SCOPED_LIMIT = 8;
const DRILL_LIMIT = 25;
const TOP_HITS = 4;
const STRONG_COMMAND = 80;

const BADGE_TONES = new Set(['success', 'critical', 'warning', 'attention', 'info']);

const SIGNAL_LABEL = {
  ogm: (signal) => `Structured communication${signal.invoiceNumber ? ` → invoice ${signal.invoiceNumber}` : ''}`,
  amount: (signal) => `Amount ${formatMoney(signal.value)}`,
  iban: (signal) => `IBAN ${signal.value}`,
  vat: (signal) => `VAT number ${signal.value}`,
  email: (signal) => `E-mail ${signal.value}`,
  phone: (signal) => `Phone number ending in ${signal.value}`
};

const LINK_ICONS = { mailto: EmailIcon, tel: PhoneIcon };

// the "Today" cards of the empty palette (AdminFull only): fields of GET /api/metrics/topbar-summary
const DAY_STATS = [
  { key: 'invoiced', label: 'Invoiced today', value: 'outgoingInvoicesValueToday', count: 'outgoingInvoicesCountToday', nouns: ['invoice', 'invoices'], url: '/invoices', icon: ReceiptEuroIcon },
  { key: 'sold', label: 'Sold today', value: 'salesValueToday', count: 'salesCountToday', nouns: ['sales order', 'sales orders'], url: '/sales', icon: OrderIcon }
];

const DAY_FORMAT = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
const WHOLE_EUROS = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 });

function formatMoney(amount, currency = 'EUR') {
  try {
    return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: currency || 'EUR' }).format(amount);
  } catch {
    return `${amount} ${currency || ''}`.trim();
  }
}

const formatDate = (date) => (/^\d{4}-\d{2}-\d{2}/.test(date || '') ? `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)}` : '');

// detail values come as text: amounts and ISO dates are shown the way the rows show them
function formatDetail(detail, currency) {
  if (/^-?\d+(\.\d+)?$/.test(detail.value) && /total|open|price|amount/i.test(detail.label)) return formatMoney(Number(detail.value), currency);
  if (/^\d{4}-\d{2}-\d{2}$/.test(detail.value)) return formatDate(detail.value);
  return detail.value;
}

const itemKey = (item) => `${item.type}:${item.id}`;

const isEditable = (element) => Boolean(element && (element.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)));

const absoluteUrl = (url) => (url && url.startsWith('/') ? `${window.location.origin}${url}` : url);

function parseInput(raw, scopeKey) {
  const value = raw.trimStart();
  const scope = PREFIX_SCOPES[value[0]];
  if (scope) return { scopeKey: scope, text: value.slice(1).trim(), prefix: value[0] };
  return { scopeKey, text: raw.trim(), prefix: null };
}

// the create actions of the company page (pages/company/index.jsx) that only need the company itself
function companyCreateActions(item) {
  const extra = item.extra || {};
  if (item.type !== 'company' || !extra.landId) return [];
  const language = extra.taalId ? `&TaalID=${extra.taalId}` : '';
  const actions = [
    { label: 'New contact', url: `/contacts/new?KLANR=${item.id}${language}` },
    { label: 'New project', url: `/projects/new?firnr=${item.id}` },
    { label: 'New machine', url: `/machines/new?Firmaid=${item.id}` }
  ];
  if (extra.supplier) {
    actions.push({ label: 'New inquiry', url: `/inquiries/new?LeverancierID=${item.id}${language}` });
    actions.push({ label: 'New purchase order', url: `/purchases/new?LeverancierID=${item.id}${language}` });
  }
  return actions;
}

function Highlighted({ text, words }) {
  if (!text) return null;
  return highlight(text, words).map((part, index) =>
    part.hit ? (
      <mark key={index} className="cmdk-mark">
        {part.text}
      </mark>
    ) : (
      <React.Fragment key={index}>{part.text}</React.Fragment>
    )
  );
}

function Tile({ icon, family, size }) {
  return (
    <span className={`cmdk-tile cmdk-tile--${family || 'neutral'}${size === 'large' ? ' cmdk-tile--large' : ''}`} aria-hidden="true">
      <Icon source={icon} />
    </span>
  );
}

function Badges({ badges }) {
  if (!badges?.length) return null;
  return badges.slice(0, 2).map((badge) => (
    <span key={badge.label} className={`cmdk-badge cmdk-badge--${BADGE_TONES.has(badge.tone) ? badge.tone : 'neutral'}`}>
      {badge.label}
    </span>
  ));
}

function rowVisual(row) {
  if (row.kind === 'command') {
    const { command } = row;
    return { icon: command.icon, family: command.kind === 'create' ? 'create' : 'command', title: command.title, subtitle: command.subtitle };
  }
  if (row.kind === 'query') return { icon: SearchRecentIcon, family: 'command', title: row.query, subtitle: 'Recent search' };
  const info = typeInfo(row.item.type);
  return { icon: info.icon, family: info.family, title: row.item.title, subtitle: row.item.subtitle };
}

function ResultRow({ row, active, words, onHover, onOpen, setRef }) {
  const visual = rowVisual(row);
  const item = row.kind === 'record' || row.kind === 'recent' ? row.item : null;
  const info = item ? typeInfo(item.type) : null;
  return (
    <div
      ref={setRef}
      id={`cmdk-row-${row.key}`}
      role="option"
      aria-selected={active}
      className={`cmdk-row${active ? ' cmdk-row--active' : ''}`}
      onMouseMove={onHover}
      onMouseDown={(event) => event.preventDefault()}
      onClick={(event) => onOpen(row, { newTab: event.metaKey || event.ctrlKey })}
      onAuxClick={(event) => {
        if (event.button === 1) onOpen(row, { newTab: true });
      }}
    >
      <Tile icon={visual.icon} family={visual.family} />
      <div className="cmdk-row-text">
        <div className="cmdk-row-title">
          <Highlighted text={visual.title} words={words} />
          {row.kind === 'recent' && info ? <span className="cmdk-row-kind">{info.label}</span> : null}
        </div>
        {visual.subtitle && !row.hideSubtitle ? (
          <div className="cmdk-row-subtitle">
            {row.kind === 'record' && row.showType && info ? <span className="cmdk-row-type">{info.label} · </span> : null}
            <Highlighted text={visual.subtitle} words={words} />
          </div>
        ) : null}
        {item?.match?.text ? (
          <div className="cmdk-row-match">
            <span className="cmdk-row-match-field">{item.match.field}</span>
            <Highlighted text={item.match.text} words={words} />
          </div>
        ) : null}
      </div>
      <div className="cmdk-row-meta">
        {item ? <Badges badges={item.badges} /> : null}
        {item?.amount !== undefined && item?.amount !== null ? <span className="cmdk-row-amount">{formatMoney(item.amount, item.currency)}</span> : null}
        {item?.date ? <span className="cmdk-row-date">{formatDate(item.date)}</span> : null}
        {active ? (
          <span className="cmdk-row-enter" aria-hidden="true">
            <Icon source={ReturnIcon} />
          </span>
        ) : null}
      </div>
    </div>
  );
}

function SectionHeader({ section, onDrill }) {
  const deep = DEEP_GROUPS[section.groupKey];
  return (
    <div className="cmdk-section-title">
      {deep ? (
        <span className="cmdk-section-icon">
          <Icon source={deep.icon} />
        </span>
      ) : null}
      <span>{section.title}</span>
      {section.count ? <span className="cmdk-section-count">{section.count}</span> : null}
      {section.timedOut ? <span className="cmdk-section-note">took too long, partial</span> : null}
      {section.hasMore && onDrill ? (
        <button type="button" className="cmdk-link-button" onMouseDown={(event) => event.preventDefault()} onClick={() => onDrill(section)}>
          Show more
        </button>
      ) : null}
    </div>
  );
}

// one slim row of cards: the date, "incl. VAT" and the exact amount are in the tooltip
function DayStats({ stats, onGo }) {
  const day = DAY_FORMAT.format(new Date());
  return (
    <div className="cmdk-section cmdk-day" role="group" aria-label={`Today, ${day}`}>
      {DAY_STATS.map((stat) => {
        const amount = Number(stats?.[stat.value]) || 0;
        const count = Number(stats?.[stat.count]) || 0;
        return (
          <button
            key={stat.key}
            type="button"
            className="cmdk-day-card"
            title={stats ? `${stat.label} (${day}), incl. VAT: ${formatMoney(amount)}` : undefined}
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => onGo(stat.url, { newTab: event.metaKey || event.ctrlKey })}
          >
            <Tile icon={stat.icon} family="sales" />
            <span className="cmdk-day-text">
              {stats ? <span className="cmdk-day-value">{WHOLE_EUROS.format(amount)}</span> : <span className="cmdk-skeleton" />}
              <span className="cmdk-day-sub">
                {stat.label}
                {stats ? ` · ${count ? `${count} ${stat.nouns[count === 1 ? 0 : 1]}` : 'none yet'}` : ''}
              </span>
            </span>
            <span className="cmdk-day-go" aria-hidden="true">
              <Icon source={ArrowRightIcon} />
            </span>
          </button>
        );
      })}
    </div>
  );
}

function PreviewAction({ icon, label, hint, onClick, href }) {
  const content = (
    <>
      <span className="cmdk-action-icon">
        <Icon source={icon} />
      </span>
      <span className="cmdk-action-label">{label}</span>
      {hint ? <kbd className="cmdk-kbd cmdk-kbd--small">{hint}</kbd> : null}
    </>
  );
  if (href) {
    return (
      <a className="cmdk-action" href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" onMouseDown={(event) => event.preventDefault()}>
        {content}
      </a>
    );
  }
  return (
    <button type="button" className="cmdk-action" onMouseDown={(event) => event.preventDefault()} onClick={onClick}>
      {content}
    </button>
  );
}

function Preview({ row, words, onOpen, onCopy, onGo }) {
  if (!row) {
    return (
      <div className="cmdk-preview-empty">
        <Icon source={LightbulbIcon} tone="subdued" />
        <p>Type a name, a document number, an OGM, an IBAN, a VAT or phone number, or an amount like 1.234,56.</p>
      </div>
    );
  }
  if (row.kind === 'command' || row.kind === 'query') {
    const visual = rowVisual(row);
    return (
      <div className="cmdk-preview-inner">
        <div className="cmdk-preview-head">
          <Tile icon={visual.icon} family={visual.family} size="large" />
          <div>
            <div className="cmdk-preview-type">{row.kind === 'query' ? 'Recent search' : row.command.section}</div>
            <div className="cmdk-preview-title">{visual.title}</div>
            {row.kind === 'command' && row.command.url ? <div className="cmdk-preview-subtitle">{row.command.url}</div> : null}
          </div>
        </div>
        <div className="cmdk-preview-actions">
          <PreviewAction icon={ArrowRightIcon} label={row.kind === 'query' ? 'Search again' : row.command.run ? 'Run' : 'Open'} hint="↵" onClick={() => onOpen(row)} />
          {row.kind === 'command' && row.command.url ? <PreviewAction icon={ExternalIcon} label="Open in new tab" hint={`${MOD_KEY}↵`} onClick={() => onOpen(row, { newTab: true })} /> : null}
        </div>
      </div>
    );
  }
  const item = row.item;
  const info = typeInfo(item.type);
  const createActions = companyCreateActions(item);
  return (
    <div className="cmdk-preview-inner">
      <div className="cmdk-preview-head">
        <Tile icon={info.icon} family={info.family} size="large" />
        <div className="cmdk-preview-head-text">
          <div className="cmdk-preview-type">{info.label}</div>
          <div className="cmdk-preview-title">
            <Highlighted text={item.title} words={words} />
          </div>
          {item.subtitle ? (
            <div className="cmdk-preview-subtitle">
              <Highlighted text={item.subtitle} words={words} />
            </div>
          ) : null}
        </div>
      </div>
      {item.badges?.length || item.amount !== undefined ? (
        <div className="cmdk-preview-badges">
          <Badges badges={item.badges} />
          {item.amount !== undefined && item.amount !== null ? <span className="cmdk-preview-amount">{formatMoney(item.amount, item.currency)}</span> : null}
        </div>
      ) : null}
      {item.match?.text ? (
        <div className="cmdk-preview-match">
          <div className="cmdk-preview-match-field">Found in {item.match.field.toLowerCase()}</div>
          <div>
            <Highlighted text={item.match.text} words={words} />
          </div>
        </div>
      ) : null}
      {item.details?.length ? (
        <dl className="cmdk-preview-details">
          {item.details.map((detail) => (
            <React.Fragment key={detail.label}>
              <dt>{detail.label}</dt>
              <dd>{formatDetail(detail, item.currency)}</dd>
            </React.Fragment>
          ))}
        </dl>
      ) : null}
      <div className="cmdk-preview-actions">
        <PreviewAction icon={ArrowRightIcon} label="Open" hint="↵" onClick={() => onOpen(row)} />
        <PreviewAction icon={ExternalIcon} label="Open in new tab" hint={`${MOD_KEY}↵`} onClick={() => onOpen(row, { newTab: true })} />
        {item.number ? <PreviewAction icon={ClipboardIcon} label={`Copy ${item.number}`} hint={`${MOD_KEY}⇧C`} onClick={() => onCopy(item.number, 'Number')} /> : null}
        <PreviewAction icon={LinkIcon} label="Copy link" hint={`${MOD_KEY}⇧L`} onClick={() => onCopy(absoluteUrl(item.url), 'Link')} />
        {item.company && item.type !== 'company' ? <PreviewAction icon={OrganizationIcon} label={`Open ${item.company.name}`} onClick={() => onGo(`/companies/${item.company.id}`)} /> : null}
        {(item.links || []).map((link) => (
          <PreviewAction key={link.url} icon={LINK_ICONS[link.url.split(':')[0]] || (item.type === 'file' ? FileIcon : GlobeIcon)} label={link.label} href={link.url} />
        ))}
        {createActions.map((action) => (
          <PreviewAction key={action.url} icon={PlusCircleIcon} label={action.label} onClick={() => onGo(action.url)} />
        ))}
      </div>
    </div>
  );
}

function PaletteDialog({ initialQuery, onClose }) {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const pickingplaces = useLookupStore((state) => state.pickingplaces);

  const [input, setInput] = useState(initialQuery || '');
  const [scopeKey, setScopeKey] = useState('all');
  const [drill, setDrill] = useState(null); // { key, title } of a group opened with "Show more"
  const [activeKey, setActiveKey] = useState(null);
  const [notice, setNotice] = useState(null);
  const [recentRecords, setRecentRecords] = useState(() => loadRecentRecords(user?.id));
  const recentQueries = useMemo(() => loadRecentQueries(user?.id), [user?.id]);
  const isFullAdmin = user?.role === userRoles.AdminFull;
  const dayStats = useDayStats(user?.id, isFullAdmin);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const scopesRef = useRef(null);
  const rowRefs = useRef(new Map());
  const userMoved = useRef(false);
  const pointer = useRef({ x: -1, y: -1 });

  const parsed = parseInput(input, scopeKey);
  const scope = scopeByKey(parsed.scopeKey);
  const text = parsed.text;
  const words = useMemo(() => queryWords(text), [text]);
  const commands = useMemo(() => buildCommands(user, { pickingplaces }), [user, pickingplaces]);

  const searchTypes = drill ? [drill.key] : scope.types;
  const search = useSearchStream({
    text,
    types: searchTypes,
    limit: drill ? DRILL_LIMIT : scope.types ? SCOPED_LIMIT : ALL_LIMIT,
    enabled: !(searchTypes && searchTypes.length === 0)
  });

  const sections = useMemo(() => {
    // under a section named after them ("Create", "Go to") the rows need no "Create" / "Page" subtitle
    const commandRows = (list, hideSubtitle = false) => list.map((command) => ({ key: `cmd:${command.id}`, kind: 'command', command, hideSubtitle }));
    const byUrl = (urls) => urls.map((url) => commands.find((command) => command.url === url)).filter(Boolean);

    if (!text) {
      if (scope.key === 'commands') {
        const order = ['Create', 'Go to', 'Settings', 'Account'];
        return order
          .map((section) => ({
            key: `cmd-section:${section}`,
            title: section,
            rows: commandRows(
              commands.filter((command) => command.section === section),
              true
            )
          }))
          .filter((section) => section.rows.length);
      }
      const inScope = (entry) => scope.key === 'all' || (scope.types || []).includes(entry.type) || typeInfo(entry.type).scope === scope.key;
      const recent = recentRecords.filter(inScope).slice(0, 6);
      const out = [];
      if (recent.length) out.push({ key: 'recent', title: 'Recently opened', rows: recent.map((entry) => ({ key: `recent:${entry.url}`, kind: 'recent', item: entry })) });
      if (scope.key === 'all') {
        if (recentQueries.length) out.push({ key: 'queries', title: 'Recent searches', rows: recentQueries.slice(0, 3).map((query) => ({ key: `query:${query}`, kind: 'query', query })) });
        out.push({ key: 'create', title: 'Create', rows: commandRows(byUrl(QUICK_CREATE_URLS), true) });
        out.push({ key: 'goto', title: 'Go to', rows: commandRows(byUrl(QUICK_PAGE_URLS), true) });
      }
      return out.filter((section) => section.rows.length);
    }

    const matches = scope.commands && !drill ? rankCommands(text, commands, scope.key === 'commands' ? 40 : 5) : [];
    const strong = matches.length && matches[0].score >= STRONG_COMMAND;
    const commandSection = matches.length ? { key: 'commands', title: 'Pages & actions', rows: commandRows(matches.map((match) => match.command)) } : null;
    if (scope.key === 'commands') return commandSection ? [commandSection] : [];

    const top = [];
    if (!drill) {
      for (const group of search.groups) {
        for (const item of group.items) {
          if (top.length >= TOP_HITS) break;
          if ((item.exact || item.rank === 0) && !top.some((existing) => itemKey(existing) === itemKey(item))) top.push(item);
        }
      }
    }
    const topKeys = new Set(top.map(itemKey));
    const groupSections = search.groups
      .map((group) => {
        const rows = group.items
          .filter((item) => group.phase === 'deep' || !topKeys.has(itemKey(item)))
          .map((item) => ({ key: `item:${group.key}:${itemKey(item)}`, kind: 'record', item, showType: Boolean(DEEP_GROUPS[group.key]) && group.key !== 'files' }));
        return {
          key: `group:${group.key}`,
          groupKey: group.key,
          title: groupLabel(group),
          phase: group.phase,
          hasMore: group.hasMore && !drill,
          timedOut: group.timedOut,
          count: group.items.length,
          rows
        };
      })
      .filter((section) => section.rows.length);
    const topSection = top.length ? { key: 'top', title: 'Top hits', rows: top.map((item) => ({ key: `top:${itemKey(item)}`, kind: 'record', item, showType: true })) } : null;
    const fast = groupSections.filter((section) => section.phase !== 'deep');
    const deep = groupSections.filter((section) => section.phase === 'deep');
    return (strong ? [commandSection, topSection, ...fast, ...deep] : [topSection, ...fast, commandSection, ...deep]).filter(Boolean);
  }, [text, scope, drill, commands, search.groups, recentRecords, recentQueries]);

  const rows = useMemo(() => sections.flatMap((section) => section.rows), [sections]);
  const activeIndex = rows.findIndex((row) => row.key === activeKey);
  const activeRow = activeIndex >= 0 ? rows[activeIndex] : null;

  // new query or filter: the first row is active again until the user moves
  useEffect(() => {
    userMoved.current = false;
  }, [text, scope.key, drill]);

  useEffect(() => {
    if (!rows.length) {
      if (activeKey !== null) setActiveKey(null);
      return;
    }
    if (!userMoved.current || activeIndex < 0) setActiveKey(rows[0].key);
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    const element = activeKey ? rowRefs.current.get(activeKey) : null;
    if (element) element.scrollIntoView({ block: 'nearest' });
  }, [activeKey]);

  // Tab can pick a filter that is scrolled out of a narrow pill row
  useLayoutEffect(() => {
    scopesRef.current?.querySelector('.cmdk-scope--active')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [scope.key]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 1600);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const go = useCallback(
    (url, { newTab = false } = {}) => {
      if (!url) return;
      if (newTab) {
        window.open(url, '_blank', 'noopener');
        return;
      }
      onClose();
      navigate(url);
    },
    [navigate, onClose]
  );

  const openRow = useCallback(
    (row, { newTab = false } = {}) => {
      if (!row) return;
      if (row.kind === 'query') {
        setInput(row.query);
        inputRef.current?.focus();
        return;
      }
      if (row.kind === 'command') {
        if (row.command.run) {
          onClose();
          row.command.run({ logout });
          return;
        }
        go(row.command.url, { newTab });
        return;
      }
      const item = row.item;
      if (!item.url) return;
      rememberRecord(user?.id, item);
      if (text) rememberQuery(user?.id, input.trim());
      if (newTab) setRecentRecords(loadRecentRecords(user?.id));
      go(item.url, { newTab });
    },
    [go, input, logout, onClose, text, user?.id]
  );

  const copy = useCallback(async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setNotice(`${label} copied`);
    } catch {
      setNotice('Copying is blocked in this browser');
    }
  }, []);

  const moveActive = (delta) => {
    if (!rows.length) return;
    userMoved.current = true;
    const from = activeIndex < 0 ? (delta > 0 ? -1 : 0) : activeIndex;
    const next = (((from + delta) % rows.length) + rows.length) % rows.length;
    setActiveKey(rows[next].key);
  };

  const cycleScope = (direction) => {
    setDrill(null);
    const current = SCOPES.findIndex((entry) => entry.key === scope.key);
    const next = SCOPES[(current + direction + SCOPES.length) % SCOPES.length];
    // a typed prefix ("> ", "@ ") picks the filter itself: drop it when moving on
    if (parsed.prefix) setInput(text);
    setScopeKey(next.key);
  };

  const onKeyDown = (event) => {
    const mod = event.metaKey || event.ctrlKey;
    if (event.key === 'ArrowDown' || (event.ctrlKey && event.key === 'n')) {
      event.preventDefault();
      moveActive(1);
    } else if (event.key === 'ArrowUp' || (event.ctrlKey && event.key === 'p')) {
      event.preventDefault();
      moveActive(-1);
    } else if (event.key === 'PageDown') {
      event.preventDefault();
      moveActive(5);
    } else if (event.key === 'PageUp') {
      event.preventDefault();
      moveActive(-5);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      openRow(activeRow, { newTab: mod });
    } else if (event.key === 'Tab') {
      event.preventDefault();
      cycleScope(event.shiftKey ? -1 : 1);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (drill) setDrill(null);
      else onClose();
    } else if (event.key === 'Backspace' && !input) {
      if (drill) setDrill(null);
      else if (scopeKey !== 'all') setScopeKey('all');
    } else if (mod && event.shiftKey && event.key.toLowerCase() === 'c' && activeRow?.item) {
      event.preventDefault();
      copy(activeRow.item.number || activeRow.item.title, activeRow.item.number ? 'Number' : 'Title');
    } else if (mod && event.shiftKey && event.key.toLowerCase() === 'l' && activeRow?.item) {
      event.preventDefault();
      copy(absoluteUrl(activeRow.item.url), 'Link');
    }
  };

  const onRowHover = (row) => (event) => {
    // only a real mouse movement moves the selection, not the list scrolling under a resting pointer
    if (event.clientX === pointer.current.x && event.clientY === pointer.current.y) return;
    pointer.current = { x: event.clientX, y: event.clientY };
    if (row.key !== activeKey) {
      userMoved.current = true;
      setActiveKey(row.key);
    }
  };

  const scopeCounts = useMemo(() => {
    const counts = {};
    if (!text || drill) return counts;
    for (const group of search.groups) {
      for (const item of group.items) {
        const key = group.key === 'files' ? 'files' : typeInfo(item.type).scope;
        counts[key] = (counts[key] || 0) + 1;
      }
    }
    return counts;
  }, [search.groups, text, drill]);

  const loading = search.status === 'loading';
  const signals = (search.query?.signals || []).filter((signal) => SIGNAL_LABEL[signal.kind] && search.text === text);
  const resultCount = rows.filter((row) => row.kind === 'record').length;
  const serverSearching = text.length >= 2 && !(searchTypes && searchTypes.length === 0);

  let status = null;
  if (search.status === 'expired') status = { tone: 'critical', text: 'Your session expired: reload the page to sign in again.' };
  else if (search.status === 'error') status = { tone: 'critical', text: 'Search failed. Try again.' };
  else if (loading && search.groups.length && search.deepPlanned && !search.stale) status = { tone: 'busy', text: 'Searching lines, notes & files…' };
  else if (loading) status = { tone: 'busy', text: 'Searching…' };
  else if (search.status === 'done' && serverSearching) status = { tone: 'done', text: `${search.results} result${search.results === 1 ? '' : 's'} · ${search.ms} ms` };

  const showNoResults = Boolean(text) && !rows.length && (!serverSearching || search.status === 'done');

  return (
    <div
      className="cmdk-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="cmdk-panel" role="dialog" aria-modal="true" aria-label="Search everything">
        <div className="cmdk-header">
          <span className="cmdk-header-icon">
            <Icon source={SearchIcon} />
          </span>
          {drill ? (
            <button type="button" className="cmdk-chip" onClick={() => setDrill(null)} title="Back to all results">
              {drill.title}
              <Icon source={XSmallIcon} />
            </button>
          ) : null}
          <input
            ref={inputRef}
            className="cmdk-input"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={scope.key === 'commands' ? 'Search pages and actions…' : 'Search companies, documents, products, numbers, amounts…'}
            autoFocus
            spellCheck={false}
            autoComplete="off"
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
            aria-activedescendant={activeRow ? `cmdk-row-${activeRow.key}` : undefined}
          />
          {loading ? <span className="cmdk-spinner" aria-label="Searching" /> : null}
          {input ? (
            <button type="button" className="cmdk-clear" onClick={() => setInput('')} aria-label="Clear search" onMouseDown={(event) => event.preventDefault()}>
              <Icon source={XSmallIcon} />
            </button>
          ) : null}
          <kbd className="cmdk-kbd cmdk-hide-mobile">esc</kbd>
        </div>

        <div className="cmdk-scopes" role="tablist" aria-label="Filter" ref={scopesRef}>
          {SCOPES.map((entry) => (
            <button
              key={entry.key}
              type="button"
              role="tab"
              aria-selected={!drill && scope.key === entry.key}
              className={`cmdk-scope${!drill && scope.key === entry.key ? ' cmdk-scope--active' : ''}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setDrill(null);
                if (parsed.prefix) setInput(text);
                setScopeKey(entry.key);
              }}
            >
              {entry.label}
              {scopeCounts[entry.key] ? <span className="cmdk-scope-count">{scopeCounts[entry.key]}</span> : null}
            </button>
          ))}
        </div>

        {signals.length ? (
          <div className="cmdk-signals">
            {signals.map((signal) => (
              <span key={signal.kind} className="cmdk-signal">
                {SIGNAL_LABEL[signal.kind](signal)}
              </span>
            ))}
          </div>
        ) : null}

        <div className="cmdk-body">
          <div className={`cmdk-list${search.stale ? ' cmdk-list--stale' : ''}`} id="cmdk-list" role="listbox" ref={listRef}>
            {isFullAdmin && !text && scope.key === 'all' && !drill && (dayStats.stats || !dayStats.failed) ? <DayStats stats={dayStats.stats} onGo={go} /> : null}
            {sections.map((section) => (
              <div className="cmdk-section" key={section.key} role="group" aria-label={section.title}>
                <SectionHeader section={section} onDrill={(target) => setDrill({ key: target.groupKey, title: target.title })} />
                {section.rows.map((row) => (
                  <ResultRow
                    key={row.key}
                    row={row}
                    active={row.key === activeKey}
                    words={words}
                    onHover={onRowHover(row)}
                    onOpen={openRow}
                    setRef={(element) => {
                      if (element) rowRefs.current.set(row.key, element);
                      else rowRefs.current.delete(row.key);
                    }}
                  />
                ))}
                {section.key === 'recent' ? (
                  <button
                    type="button"
                    className="cmdk-link-button cmdk-section-footer"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      recentRecords.forEach((entry) => forgetRecord(user?.id, entry.url));
                      setRecentRecords([]);
                    }}
                  >
                    Clear recent
                  </button>
                ) : null}
              </div>
            ))}

            {showNoResults ? (
              <div className="cmdk-empty">
                <Icon source={SearchIcon} tone="subdued" />
                <p className="cmdk-empty-title">No results for “{text}”</p>
                <p className="cmdk-empty-text">Try fewer words, part of a name, a document number or an amount like 1.234,56. Type &gt; to search pages and actions.</p>
              </div>
            ) : null}

            {!text && scope.key === 'all' ? (
              <div className="cmdk-tips">
                <span>
                  <kbd className="cmdk-kbd cmdk-kbd--small">&gt;</kbd> pages & actions
                </span>
                <span>
                  <kbd className="cmdk-kbd cmdk-kbd--small">@</kbd> companies & contacts
                </span>
                <span>
                  <kbd className="cmdk-kbd cmdk-kbd--small">⇥</kbd> filters
                </span>
                <span>Numbers, OGM, IBAN, VAT, phone and amounts are recognised</span>
              </div>
            ) : null}

            {!text && scope.key !== 'all' && scope.key !== 'commands' && !sections.length ? (
              <div className="cmdk-empty">
                <Icon source={SearchIcon} tone="subdued" />
                <p className="cmdk-empty-text">Type to search {scope.label.toLowerCase()}.</p>
              </div>
            ) : null}
          </div>

          <aside className="cmdk-preview" aria-label="Details">
            <Preview row={activeRow} words={words} onOpen={openRow} onCopy={copy} onGo={(url) => go(url)} />
          </aside>
        </div>

        <div className="cmdk-footer">
          <div className="cmdk-footer-keys cmdk-hide-mobile">
            <span>
              <kbd className="cmdk-kbd cmdk-kbd--small">↑</kbd>
              <kbd className="cmdk-kbd cmdk-kbd--small">↓</kbd> navigate
            </span>
            <span>
              <kbd className="cmdk-kbd cmdk-kbd--small">↵</kbd> open
            </span>
            <span>
              <kbd className="cmdk-kbd cmdk-kbd--small">{MOD_KEY}↵</kbd> new tab
            </span>
            <span>
              <kbd className="cmdk-kbd cmdk-kbd--small">⇥</kbd> filter
            </span>
          </div>
          <div className={`cmdk-status${status ? ` cmdk-status--${status.tone}` : ''}`} aria-live="polite">
            {notice ? (
              <span className="cmdk-notice">{notice}</span>
            ) : status ? (
              <>
                {status.tone === 'critical' ? <Icon source={AlertTriangleIcon} tone="critical" /> : null}
                {status.tone === 'busy' ? <span className="cmdk-spinner cmdk-spinner--small" /> : null}
                <span>{status.text}</span>
              </>
            ) : text ? (
              <span>{resultCount ? `${resultCount} shown` : ''}</span>
            ) : (
              <span>
                <Icon source={ClockIcon} tone="subdued" /> {SHORTCUT_LABEL} opens this from anywhere
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/*
  Always mounted (PageWrapper): owns the keyboard shortcuts and renders the dialog in a portal while open.
*/
export default function CommandPalette() {
  const isOpen = useCommandPaletteStore((state) => state.isOpen);
  const initialQuery = useCommandPaletteStore((state) => state.initialQuery);
  const open = useCommandPaletteStore((state) => state.open);
  const close = useCommandPaletteStore((state) => state.close);
  const toggle = useCommandPaletteStore((state) => state.toggle);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    const onKeyDown = (event) => {
      const key = event.key?.toLowerCase();
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && key === 'k') {
        // capture phase: before rich-text editors and Polaris handlers see it
        event.preventDefault();
        event.stopPropagation();
        toggle();
      } else if (event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey && !useCommandPaletteStore.getState().isOpen && !isEditable(event.target)) {
        event.preventDefault();
        open('');
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open, toggle]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const restoreTarget = takeFocusBeforeOpen();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      if (restoreTarget && document.contains(restoreTarget) && typeof restoreTarget.focus === 'function') restoreTarget.focus({ preventScroll: true });
    };
  }, [isOpen]);

  if (!isOpen || !user) return null;
  return createPortal(<PaletteDialog initialQuery={initialQuery} onClose={close} />, document.body);
}
